import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin } from 'rxjs';
import { CisProSearchComponent } from '../cispro-search/cispro-search.component';
import { CisProSearchResult } from '../../services/cispro-search.service';
import { ProjectDetailsService, SpacerQaqcTitleDto } from '../../services/project-details.service';
import { AddTestRequestDto, SpacerService } from '../../services/spacer.service';

export interface AddTestDialogData {
  spacerId: number;
  projectId: number;
}

export interface AddTestDialogResult {
  testType: string;
  formData: any;
}

interface RatioRow {
  material1Percent: number | null;
  type1: number | null;
  material2Percent: number | null;
  type2: number | null;
}

interface ReadingRow {
  temp: number | null;
  pressure: number | null;
  totalTime: number | null;
}

@Component({
  selector: 'app-add-test-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './add-test-dialog.component.html',
  styleUrls: ['./add-test-dialog.component.css']
})
export class AddTestDialogComponent implements OnInit {
  isLoading = true;
  isSaving = false;

  readonly defaultRatiosTooltip = [
    'Default Ratio Combinations:',
    '0% Mud / 100% (Mud only)',
    '5% Mud / 95% Spacer',
    '25% Mud / 75% Spacer',
    '50% Mud / 50% Spacer',
    '75% Mud / 25% Spacer',
    '95% Mud / 5% Spacer',
    '0% Spacer / 100% (Spacer only)',
    '5% Cement / 95% Spacer',
    '25% Cement / 75% Spacer',
    '50% Cement / 50% Spacer',
    '75% Cement / 25% Spacer',
    '95% Cement / 5% Spacer',
    '0% Cement / 100% (Cement only)',
    '5% Cement / 95% Mud',
    '25% Cement / 75% Mud',
    '50% Cement / 50% Mud',
    '75% Cement / 25% Mud',
    '95% Cement / 5% Mud',
  ].join('\n');
  selectedTestType: string = '';
  testTypes: string[] = [];

  // SpacerCompatibility fields
  temperature: number | null = null;
  mudCisproContainer: string = '';
  slurry: string = '';
  slurryOptions: { slurryId: number; slurryName: string }[] = [];
  isLoadingSlurries = false;
  ratios: RatioRow[] = [];
  compatibilityNotes: string = '';
  materialTypes: { id: number; name: string }[] = [
    { id: 1, name: 'Mud' },
    { id: 2, name: 'Cement' },
    { id: 3, name: 'Spacer' },
  ];

  private wellId: number | null = null;

  // SpacerSSST fields
  ssstTemperature: number | null = null;
  ssstMudCisproContainer: string = '';
  ssstNotes: string = '';

  // SpacerHpHtRheology fields
  readings: ReadingRow[] = [];
  rheologyNotes: string = '';
  equipmentTypes: { id: number; name: string }[] = [
    { id: 1, name: 'Fan77' },
    { id: 2, name: 'Chann7600' },
    { id: 3, name: 'Chann7550' },
  ];
  equipmentTypeId: number | null = 1;

  // SpacerQaQc fields
  qaqcTemperature: number | null = null;
  qaqcTitles: SpacerQaqcTitleDto[] = [];
  qaqcNotes: string = '';

  constructor(
    public dialogRef: MatDialogRef<AddTestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddTestDialogData,
    private spacerService: SpacerService,
    private projectDetailsService: ProjectDetailsService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    forkJoin({
      tests: this.spacerService.getSpacerTestInputs(this.data.spacerId),
      project: this.projectDetailsService.getProjectById(this.data.projectId)
    }).subscribe({
      next: ({ tests, project }) => {
        // Rheology always shows; the other 3 show only when not yet configured
        this.testTypes = [
          'SpacerHpHtRheology',
          ...(tests?.spacerCompatibility == null ? ['SpacerCompatibility'] : []),
          ...(tests?.spacerQaQc         == null ? ['SpacerQaQc']          : []),
          ...(tests?.spacerSsst         == null ? ['SpacerSSST']           : []),
        ];

        this.qaqcTitles = project?.spacerQAQCTitles ?? [];
        this.wellId = project?.wellId ?? null;
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  onTestTypeChange(): void {
    if (this.selectedTestType === 'SpacerCompatibility' && this.slurryOptions.length === 0 && this.wellId) {
      this.isLoadingSlurries = true;
      this.spacerService.getSlurriesForWell(this.wellId).subscribe({
        next: (data) => { this.slurryOptions = data; this.isLoadingSlurries = false; },
        error: () => { this.isLoadingSlurries = false; }
      });
    }
  }

  addRatio(): void {
    this.ratios.push({
      material1Percent: null,
      type1: null,
      material2Percent: null,
      type2: null
    });
  }

  removeRatio(index: number): void {
    this.ratios.splice(index, 1);
  }

  addReading(): void {
    this.readings.push({
      temp: null,
      pressure: null,
      totalTime: null
    });
  }

  removeReading(index: number): void {
    this.readings.splice(index, 1);
  }

  searchCispro(field: 'compatibility' | 'ssst'): void {
    const ref = this.dialog.open(CisProSearchComponent, {
      width: '90%',
      maxWidth: '1300px',
      maxHeight: '85vh',
      panelClass: 'cispro-search-dialog'
    });

    ref.afterClosed().subscribe((result: CisProSearchResult | undefined) => {
      if (result) {
        if (field === 'compatibility') {
          this.mudCisproContainer = result.materialName;
        } else {
          this.ssstMudCisproContainer = result.materialName;
        }
      }
    });
  }

  removeCispro(field: 'compatibility' | 'ssst'): void {
    if (field === 'compatibility') {
      this.mudCisproContainer = '';
    } else {
      this.ssstMudCisproContainer = '';
    }
  }

  get isFormValid(): boolean {
    if (this.selectedTestType === 'SpacerCompatibility') {
      return this.temperature != null && this.slurry !== '';
    }
    if (this.selectedTestType === 'SpacerSSST') {
      return this.ssstTemperature != null;
    }
    if (this.selectedTestType === 'SpacerQaQc') {
      return this.qaqcTemperature != null && this.qaqcTitles.length > 0;
    }
    return true;
  }

  onAdd(): void {
    if (!this.selectedTestType) {
      return;
    }

    const body = this.buildPayload();
    this.isSaving = true;

    this.spacerService.addTest(this.data.spacerId, body).subscribe({
      next: () => this.dialogRef.close({ testType: this.selectedTestType, formData: body }),
      error: (err) => {
        console.error('Failed to add test', err);
        this.isSaving = false;
      }
    });
  }

  private buildPayload(): AddTestRequestDto {
    const body: AddTestRequestDto = { testType: this.selectedTestType };

    if (this.selectedTestType === 'SpacerCompatibility') {
      if (this.temperature != null) body.temperature = this.temperature;
      if (this.mudCisproContainer) body.mudCisProContainer = this.mudCisproContainer;
      if (this.slurry) body.compatibilitySlurryId = Number(this.slurry);
      if (this.compatibilityNotes) body.notes = this.compatibilityNotes;
      const readings = this.ratios
        .filter(r => r.material1Percent != null || r.type1 != null || r.material2Percent != null || r.type2 != null)
        .map(r => {
          const row: NonNullable<AddTestRequestDto['compatibilityReadings']>[0] = { spacerPercent: r.material1Percent ?? 0 };
          if (r.type1 != null) row.spacerCompatibilityRatioType1 = r.type1;
          if (r.type2 != null) row.spacerCompatibilityRatioType2 = r.type2;
          return row;
        });
      if (readings.length > 0) body.compatibilityReadings = readings;

    } else if (this.selectedTestType === 'SpacerSSST') {
      if (this.ssstTemperature != null) body.temperature = this.ssstTemperature;
      if (this.ssstMudCisproContainer) body.mudCisProContainer = this.ssstMudCisproContainer;
      if (this.ssstNotes) body.notes = this.ssstNotes;
    } else if (this.selectedTestType === 'SpacerHpHtRheology') {
      if (this.equipmentTypeId != null) body.equipmentTypeId = this.equipmentTypeId;
      if (this.rheologyNotes) body.notes = this.rheologyNotes;
      const readings = this.readings
        .filter(r => r.temp != null || r.pressure != null || r.totalTime != null)
        .map(r => ({
          temperature: r.temp ?? 0,
          pressure: r.pressure ?? 0,
          time: r.totalTime ?? 0
        }));
      if (readings.length > 0) body.readings = readings;

    } else if (this.selectedTestType === 'SpacerQaQc') {
      if (this.qaqcTemperature != null) body.temperature = this.qaqcTemperature;
      if (this.qaqcNotes) body.notes = this.qaqcNotes;
      if (this.qaqcTitles.length > 0) {
        body.qaqcReadings = this.qaqcTitles.map(t => ({ titleId: t.id }));
      }
    }
    return body;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
